import React, { useEffect, useRef, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  InputBase,
  Avatar,
  Popover,
  useMediaQuery,
  useTheme,
  Button,
  Badge,
  Divider,
  Stack,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Add as AddIcon,
  Search as SearchIcon,
  NotificationsNone as NotificationsIcon,
  BookmarkBorderOutlined,
  Style,
  Close,
} from "@mui/icons-material";

import { GradientButton_ } from "../components/Buttons/GradientButton";
import { useAuth, useCurrentTalentProfile } from "../hooks";
import TalentandUserToggleMenu from "../components/Dashboard/Talent/TalentCustomMenu_updated_no";
import { useNavigate } from "react-router-dom";
import SocketNotification from "./SocketNotification";
import { Link } from "react-router-dom";
import useCurrentGTP from "../hooks/useGTP";
import { StageLinkLogo } from "../components/Branding/StageLinkLogo";

import { useGetNotification, useAllNotificationRead } from "../services/api/notification.api";
import { NotificationFullData } from "./UserNotification";
import { SocketNotificationData } from "../components/socketNotification/types";

interface HeaderProps {
  toggleDrawer: () => void;
  onClickCreate?: () => void;
  coffeeCallNotificationfromLayout?: any;
  regularCallNotificationfromLayout?: any;
  groupCallInvitationfromLayout?: any;
  callConfirmationfromLayout?: any;
  groupCallNotificationfromLayout?: any;
  callRemainderfromLayout?: any;
  setBadgeVisible: (visible: boolean) => void;
  badgeVisible: boolean;
  socketNotification: any[];
}

/** —— Netflix-like tokens (design only; no logic changes) —— */
const COLORS = {
  bg: "#0b0b0b",
  bar: "linear-gradient(180deg, rgba(10,10,10,0.98), rgba(10,10,10,0.88))",
  panel: "#141414",
  border: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.74)",
  red: "#E50914",
  redHover: "#b20710",
};

const Header: React.FC<HeaderProps> = ({
  toggleDrawer,
  onClickCreate,
  coffeeCallNotificationfromLayout,
  regularCallNotificationfromLayout,
  groupCallInvitationfromLayout,
  callConfirmationfromLayout,
  groupCallNotificationfromLayout,
  callRemainderfromLayout,
  socketNotification,
  setBadgeVisible,
  badgeVisible,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { userInfo } = useAuth();
  const { currentGTP } = useCurrentGTP();
  const { currentProfile } = useCurrentTalentProfile();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const { data: notificationData, refetch } = useGetNotification({
      key: ["notification", currentProfile?.id, currentGTP?.id],
      params: {
        offset: 0,
        limit: 10,
        ...(currentGTP?.id && { gtp_id: currentGTP?.id }),
        ...(currentProfile?.id && { talent_id: currentProfile?.id }),
      },
      options: {
        refetchInterval: 30000,
      },
    });
  
    const { update: updateAllNotificationRead } = useAllNotificationRead({
      key: ["notificationRead"],
    });

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  // notifications popover
  const [anchorPopover, setAnchorPopover] = React.useState(null);
  const openPopover = Boolean(anchorPopover);

  useEffect(() => {
    if (
      coffeeCallNotificationfromLayout ||
      regularCallNotificationfromLayout ||
      groupCallInvitationfromLayout ||
      groupCallNotificationfromLayout ||
      callConfirmationfromLayout ||
      callRemainderfromLayout ||
      socketNotification.length > 0
    ) {
      setBadgeVisible(true);
    }
  }, [
    coffeeCallNotificationfromLayout,
    regularCallNotificationfromLayout,
    groupCallInvitationfromLayout,
    groupCallNotificationfromLayout,
    callConfirmationfromLayout,
    callRemainderfromLayout,
    socketNotification,
    setBadgeVisible,
  ]);

  useEffect(() => {
    if (socketNotification.length > 0) {
      refetch();
    }
  }, [socketNotification]);

  useEffect(() => {
    if (notificationData?.data && notificationData?.data?.length) {
      const isUnreadNotification = notificationData?.data?.some(
        (notification) => !notification.is_read,
      );
      setBadgeVisible(isUnreadNotification);
    }
  }, [notificationData?.data]);

  const handlePopoverclick = (event: any) => {
    setAnchorPopover(event.currentTarget);
    if (badgeVisible) {
      updateAllNotificationRead(
        {
          ...(currentProfile?.id && { talent_id: currentProfile?.id }),
          ...(currentGTP?.id && { gtp_id: currentGTP?.id }),
        },
        {
          onSuccess: () => {
            setBadgeVisible(false); // User viewed the popover and all notifications are marked as read
          },
          onError: (error) => {
            console.error("Failed to mark all notifications as read:", error);
          },
        },
      );
    }
  };

  const handlePopoverClose = () => {
    setAnchorPopover(null);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: COLORS.bar,
          backdropFilter: "blur(8px)",
          borderBottom: `1px solid ${COLORS.border}`,
          color: COLORS.text,
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            px: { xs: 1.5, sm: 2.5, md: 4 },
            minHeight: 72,
            gap: 1.5,
          }}
        >
          {/* Left */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={toggleDrawer} sx={{ color: COLORS.text }}>
              <MenuIcon />
            </IconButton>
            {!isMobile && (
             <StageLinkLogo/>
            )}
          </Box>

          {/* Search (hide on xs) */}
          {!isMobile && (
            <Box
              sx={{
                position: "relative",
                flex: 1,
                maxWidth: 520,
                mx: { xs: 1, md: 4 },
                backgroundColor: COLORS.panel,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                px: 2,
                height: 44,
                border: `1px solid ${COLORS.border}`,
              }}
            >
              <SearchIcon sx={{ color: COLORS.textMuted, mr: 1 }} />
              <InputBase
                placeholder="Search…"
                sx={{
                  flex: 1,
                  fontFamily: '"Lexend", sans-serif',
                  fontSize: 14,
                  color: COLORS.text,
                  "::placeholder": { color: "rgba(255,255,255,0.5)" },
                }}
              />
            </Box>
          )}

          {/* Right */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 1 : 2,
            }}
          >
            {/* {!isMobile && (
              <Stack direction="row" spacing={2}>
                {currentProfile && (
                  <GradientButton_
                    onClick={onClickCreate}
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                      backgroundColor: "transparent !important",
                      border: `1px solid ${COLORS.border}`,
                      color: COLORS.text,
                      fontWeight: 700,
                      textTransform: "none",
                      px: 2.5,
                      py: 1,
                      borderRadius: 1.5,
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.06) !important",
                        borderColor: COLORS.text,
                      },
                    }}
                  >
                    Create
                  </GradientButton_>
                )}

                {!!currentProfile && !currentGTP && (
                  <GradientButton_
                    onClick={onClickStageMode}
                    variant="contained"
                    sx={{
                      backgroundColor: "transparent !important",
                      border: `1px solid ${COLORS.border}`,
                      color: "#10B981",
                      fontWeight: 700,
                      textTransform: "none",
                      px: 2.5,
                      py: 1,
                      borderRadius: 1.5,
                      transition: "all 0.25s ease",
                      "&:hover": {
                        background:
                          "linear-gradient(90deg, #059669 0%, #10B981 100%) !important",
                        color: "#fff",
                        borderColor: "transparent",
                        boxShadow: "0 10px 24px rgba(16,185,129,0.25)",
                      },
                    }}
                  >
                    Stage Mode
                  </GradientButton_>
                )}
              </Stack>
            )} */}

            {/* {!currentProfile && !isMobile && (
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  onClick={() => navigate("/my-deck")}
                  variant="outlined"
                  startIcon={<Style />}
                  sx={{
                    border: `1px solid ${COLORS.border}`,
                    color: COLORS.text,
                    fontWeight: 600,
                    textTransform: "none",
                    px: 2,
                    py: 0.8,
                    fontSize: "0.85rem",
                    borderRadius: 1.5,
                    backgroundColor: "transparent",
                    "&:hover": {
                      borderColor: COLORS.text,
                      backgroundColor: "rgba(255,255,255,0.06)",
                    },
                  }}
                >
                  My Deck
                </Button>

                <Button
                  onClick={() => navigate("/my-bookmarks")}
                  variant="outlined"
                  startIcon={<BookmarkBorderOutlined />}
                  sx={{
                    border: `1px solid ${COLORS.border}`,
                    color: COLORS.text,
                    fontWeight: 600,
                    textTransform: "none",
                    px: 2,
                    py: 0.8,
                    fontSize: "0.85rem",
                    borderRadius: 1.5,
                    backgroundColor: "transparent",
                    "&:hover": {
                      borderColor: COLORS.text,
                      backgroundColor: "rgba(255,255,255,0.06)",
                    },
                  }}
                >
                  My Bookmarks
                </Button>
              </Stack>
            )} */}

            <>
              <IconButton
                aria-describedby={"notifications-popover"}
                onClick={handlePopoverclick}
                sx={{
                  position: "relative",
                  padding: { xs: 0.75, sm: 1 },
                  borderRadius: { xs: 1.5, sm: 1.5 },
                  backgroundColor: badgeVisible
                    ? "rgba(239, 68, 68, 0.15)"
                    : "transparent",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: badgeVisible
                      ? "rgba(239, 68, 68, 0.25)"
                      : "rgba(255, 255, 255, 0.1)",
                    transform: "scale(1.05)",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: { xs: 20, sm: 24 },
                  },
                }}
              >
                <Badge
                  color="error"
                  variant="dot"
                  invisible={!badgeVisible}
                  sx={{
                    "& .MuiBadge-badge": {
                      width: { xs: "8px", sm: "12px" },
                      height: { xs: "8px", sm: "12px" },
                      borderRadius: "50%",
                      backgroundColor: "#EF4444",
                      border: `2px solid var(--neutral-900)`,
                      boxShadow: badgeVisible
                        ? "0 0 0 2px rgba(239, 68, 68, 0.3), 0 2px 8px rgba(239, 68, 68, 0.4)"
                        : "none",
                      animation: badgeVisible ? "pulse 2s infinite" : "none",
                      top: { xs: "6px", sm: "8px" },
                      right: { xs: "6px", sm: "8px" },
                    },
                    "@keyframes pulse": {
                      "0%": {
                        boxShadow: "0 0 0 0 rgba(239, 68, 68, 0.7)",
                      },
                      "70%": {
                        boxShadow: "0 0 0 6px rgba(239, 68, 68, 0)",
                      },
                      "100%": {
                        boxShadow: "0 0 0 0 rgba(239, 68, 68, 0)",
                      },
                    },
                  }}
                >
                  <NotificationsIcon
                    sx={{
                      color: badgeVisible
                        ? "#EF4444"
                        : "var(--neutralWhite)",
                      fontSize: { xs: 20, sm: 24 },
                      transition: "all 0.3s ease",
                      filter: badgeVisible
                        ? "drop-shadow(0 2px 4px rgba(239, 68, 68, 0.3))"
                        : "none",
                    }}
                  />
                </Badge>
              </IconButton>

              {/* popover for notification */}
              <Popover
                id={"notifications-popover"}
                open={openPopover}
                anchorEl={anchorPopover}
                onClose={handlePopoverClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    maxWidth: isMobile ? "auto" : "400px",
                    mt: 1,
                    borderRadius: 3,
                    boxShadow: "0px 8px 24px rgba(0,0,0,0.15)",
                    padding: 2,
                    bgcolor: "var(--neutral-900)",
                    maxHeight: "90vh",
                    /* Scrollbar styling */
                    "&::-webkit-scrollbar": {
                      width: 5,
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor:"var(--neutral-800)",
                      borderRadius: 8,
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "var(--primary-300)",
                      borderRadius: 8,
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "var(--primary-400)",
                    },
                  },
                }}
              >
                {isMobile && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography>Notifications</Typography>
                    <IconButton
                      onClick={handlePopoverClose}
                      size="small"
                      sx={{
                        pr: 0,
                        color: "text.secondary",
                        "&:hover": {
                          color: "error.main",
                          transform: "scale(1.1)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                {(() => {
                  const fcmNotifications = [
                    coffeeCallNotificationfromLayout && {
                      type: "coffee",
                      data: coffeeCallNotificationfromLayout,
                    },
                    regularCallNotificationfromLayout && {
                      type: "regularSession",
                      data: regularCallNotificationfromLayout,
                    },
                    groupCallInvitationfromLayout && {
                      type: "groupCallInvitation",
                      data: groupCallInvitationfromLayout,
                    },
                    groupCallNotificationfromLayout && {
                      type: "groupSession",
                      data: groupCallNotificationfromLayout,
                    },
                    // callConfirmationfromLayout && {
                    //   type: "callConfirmation",
                    //   data: callConfirmationfromLayout,
                    // },
                    // callRemainderfromLayout && {
                    //   type: "callRemainder",
                    //   data: callRemainderfromLayout,
                    // },
                    // groupCallConfirmationfromLayout && {
                    //   type: "missedGroupSession",
                    //   data: groupCallConfirmationfromLayout,
                    // },
                  ].filter(Boolean);

                  const allNotifications = (notificationData?.data?.map(
                      (notification: NotificationFullData) => ({
                        data: notification,
                        isSocket: true,
                      }),
                    ) || [])

                  return allNotifications.length > 0 ? (
                    <>
                      {allNotifications.map((item, index) => (
                        <React.Fragment key={index}>
                          {item.isSocket && (
                            <SocketNotification
                              closePopover={handlePopoverClose}
                              notification={item.data}
                            />
                          )}
                          {index < allNotifications.length - 1 && (
                            <Divider sx={{ my: "10px" }} />
                          )}
                        </React.Fragment>
                      ))}
                    </>
                  ) : (
                    <Typography
                      sx={{
                        textAlign: "center",
                        p: 2,
                        color: "var(--neutral-300)",
                        font: "var(--body-xs)",
                      }}
                    >
                      No new notifications
                    </Typography>
                  );
                })()}

                <Divider sx={{ my: "10px" }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                  }}
                >
                  <Link
                    to="/notification"
                    style={{
                      font: "var(--semibold-xs)",
                      color: "var(--primary-300)",
                      textDecoration: "none",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = "var(--primary-700)";
                      e.currentTarget.style.textDecoration = "underline";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = "var(--primary-900)";
                      e.currentTarget.style.textDecoration = "none";
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.color = "var(--primary-900)";
                    }}
                    onClick={handlePopoverClose}
                  >
                    View all notifications
                  </Link>
                </Box>
              </Popover>
            </>

            {/* Avatar */}
            <IconButton onClick={handleAvatarClick}>
              <Avatar
                alt="User"
                src={
                  currentGTP?.profile_image ??
                  currentProfile?.profile_image ??
                  userInfo?.profile_image ??
                  ""
                }
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "#444",
                  fontWeight: 600,
                  fontFamily: '"Lexend", sans-serif',
                  cursor: "pointer",
                  border: `2px solid ${COLORS.border}`,
                }}
              >
                U
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Avatar Dropdown */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: "0px 12px 32px rgba(0,0,0,0.5)",
            bgcolor: COLORS.panel,
            color: COLORS.text,
            border: `1px solid ${COLORS.border}`,
          },
        }}
      >
        <TalentandUserToggleMenu />
      </Popover>
    </>
  );
};

export default Header;
